import ExampleTheme from "./themes/ExampleTheme";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import TreeViewPlugin from "./plugins/TreeViewPlugin";
import ToolbarPlugin from "./plugins/ToolbarPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { ListItemNode, ListNode } from "@lexical/list";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { TRANSFORMERS } from "@lexical/markdown";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import React, { useEffect, useState, useRef } from 'react'
import {
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  $createTextNode,
  $getRoot
} from "lexical";

import { $generateNodesFromDOM } from '@lexical/html';


import ListMaxIndentLevelPlugin from "./plugins/ListMaxIndentLevelPlugin";
import CodeHighlightPlugin from "./plugins/CodeHighlightPlugin";
import AutoLinkPlugin from "./plugins/AutoLinkPlugin";
import { BinaryOperatorToken } from "typescript";
import DocPanel from '../../documentation/DocPanel';
import useApi from '../../api';

import { ToastContainer, toast, ToastOptions } from 'react-toastify';
import toastConfig from '../../config/Toast';
import 'react-toastify/dist/ReactToastify.css';


function Placeholder() {
  return <div className="text-[#999] overflow-hidden absolute text-ellipsis top-[15px] left-[10px] text-[15px] select-none inline-block pointer-events-none p-2">Write documentation here...</div>;
}

interface EditorProps {
  content: any;
  docId: string | undefined;
  field: any;
  updateSchema: any;
  path: string[];
  mode?: string,
  modalVisible: boolean,
  setModalVisible: any
}

export default function Editor({ content, docId, field, updateSchema, path, mode, modalVisible, setModalVisible }: EditorProps) {
  const [results, setResults] = useState<any>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [contextDocs, setContextDocs] = useState<any[]>([]);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [assistantResponse, setAssistantResponse] = useState('');
  const { get, getStream, post, postStream, put, patch, remove } = useApi();
  const [messageSent, setMessageSent] = useState(false);

  const modalRef = useRef(null);
  const modalVisibleRef = React.useRef(modalVisible);
  modalVisibleRef.current = modalVisible;
  const assistantResponseRef = React.useRef(assistantResponse);
  assistantResponseRef.current = assistantResponse;

  const toggleModal = () => {
    console.log('toggle modal')
    setModalVisible(!modalVisible);
  };

  const sendMessage = async () => {
    setMessageSent(true);
  };

  // get similar docs via api
  // useEffect(() => {
  //   if (modalVisible && docId) {
  //     console.log('fetching similar docs')
  //     const data = {
  //       field,
  //       doc_id: docId
  //     }
  //     const fetchData = async () => {
  //       const response = await post(`/field-similarity`, data);
  //       console.log(response)
  //       setResults(response.data);
  //     }
  //     if (results.length === 0) {
  //       fetchData()
  //     };
  //   }
  // }, [modalVisible]);

  // Get conversation based on document id
  // useEffect(() => {
  //   const fetchDocument = async () => {
  //     if (results.length === 0) {
  //       return;
  //     }
  //     const docs = []
  //     setLoading(true);
  //     // convert result.results array to object with id as key
  //     const resultsMap: any = {};
  //     console.log(results)
  //     for (const result of results.results) {
  //       if (result['entity']['doc_id']) {
  //         resultsMap[result['entity']['doc_id']] = result;
  //       } else {
  //         resultsMap[result.id] = result;
  //       }
  //     }
  //     for (const result of results.unique_ids) {
  //       if (result === undefined || result === null || result === "") {
  //         continue;
  //       }
  //       if (resultsMap[result] && resultsMap[result]['entity']) {
  //         const docId = resultsMap[result]['entity']['doc_id'];
  //         console.log(docId)
  //         if (docId) {
  //           try {
  //             const response = await get(`/documentation/${docId}`);
  //             docs.push({ type: "aimdoc", data: response.data });
  //           } catch (error) {
  //             console.error("Error getting document:", error);
  //           }
  //         } else {
  //           const externalResult = resultsMap[result].entity;
  //           docs.push({ type: "external", app: externalResult.app, data: resultsMap[result]['entity']['ticket_data'] });
  //         }
  //       }
  //     }
  //     setDocuments(docs);
  //     setLoading(false);
  //   };
  //   fetchDocument();
  // }, [results]);

  const editorConfig = {
    // The editor theme
    theme: ExampleTheme,
    // Handling of errors during update
    editorState: content.value ? JSON.stringify(content.value) : null,
    namespace: `editor-${field.id}-true`,
    onError(error: any) {
      throw error;
    },
    // Any custom nodes go here
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      CodeNode,
      CodeHighlightNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      AutoLinkNode,
      LinkNode
    ]
  };

  return (
    <LexicalComposer initialConfig={editorConfig}>
      <div className="place-content-center fixed inset-0 bg-gray-600 bg-opacity-50 backdrop-blur-sm overflow-y-auto h-screen w-screen z-40 flex items-center justify-center">
        <div className="flex h-5/6 w-2/3 items-center justify-center mb-16">
          <div ref={modalRef} className="flex flex-row h-full w-full">
            <div className="flex flex-col w-full h-full">
              <div className="flex flex-row justify-center">
                <div className="bg-gray-200 text-gray-700 font-medium text-md flex rounded-2xl p-3 mb-2 items-center">
                  {field.name}
                </div>
                <div className="bg-gray-200 text-gray-700 text-sm flex rounded-2xl p-3 mb-2 ml-2 items-center">
                  {field.description}
                </div>
              </div>
              <ToolbarPlugin content={content} docId={docId} field={field} updateSchema={updateSchema} path={path} mode={mode} setModalVisible={setModalVisible} modalVisible={modalVisible} userInput={userInput} contextDocs={contextDocs} setUserInput={setUserInput} messageSent={messageSent} setMessageSent={setMessageSent} />
              <div className="bg-white relative h-5/6">
                <div className="flex flex-col h-full w-full">
                  <div className="flex flex-row h-full grow w-full">
                    <div className="flex w-full h-full overflow-y-auto scrollbar-thin p-2">
                      <RichTextPlugin
                        contentEditable={<ContentEditable className="min-h-[150px] text-[15px] relative tab-size-1 outline-none p-[15px_10px] caret-[#444] max-h-full w-full" />}
                        placeholder={<Placeholder />}
                        ErrorBoundary={LexicalErrorBoundary}
                      />
                    </div>
                    {/* <DocPanel results={results} isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} setContextDocs={setContextDocs} type={'field'} /> */}
                    <HistoryPlugin />
                    <AutoFocusPlugin />
                    <CodeHighlightPlugin />
                    <ListPlugin />
                    <LinkPlugin />
                    <AutoLinkPlugin />
                    <ListMaxIndentLevelPlugin maxDepth={7} />
                    <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
                  </div>
                </div>
              </div>
              <div className='flex flex-row'>
                <div className="sticky bottom-0 z-40 shadow-lg bg-gray-50 border-t pt-3 w-full rounded-b-lg">
                  <div className='flex flex-row justify-between pl-4 pb-2'>
                    <div className="relative flex w-full h-12 rounded-xl bg-white border mr-10">
                      <input
                        className="h-full w-full border-0 text-sm text-gray-700 pr-2 focus:outline-none focus:ring-1 rounded-l-xl focus:ring-blue-200 focus:border-transparent"
                        type="text"
                        id="search"
                        autoComplete="off"
                        placeholder="Type your message..."
                        value={userInput}
                        onChange={(e) => setUserInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            setLoading(true);
                            sendMessage();

                          }
                        }}
                      />
                      <div onClick={() => { setLoading(true); sendMessage(); }} className="place-items-center h-full w-30 text-gray-300 bg-blue-200 hover:bg-blue-500 text-white rounded-r-xl flex">
                        <div className="text-sm font-medium leading-6 text-white pl-2 w-16">Generate</div>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 ml-2 mr-2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                        </svg>
                      </div>

                    </div>
                    <div className="pl-4 pr-4 mt-2 mb-2 flex justify-end">
                      <button
                        onClick={() => setModalVisible(false)}
                        type="submit"
                        className="rounded-md bg-blue-900 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LexicalComposer>
  );
}
